import React, { useEffect } from 'react';
import { SelectField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import {
    PoliceDistricts as PoliceDistrictsType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const getPoliceDistrictArray = () => {
    return [
        {
            value: 'select',
            caption: 'Vælg...'
        },
        {
            value: '6035275',
            caption: ' Bornholms Politi'
        },
        {
            value: '0229648',
            caption: ' Fyns Politi'
        },
        {
            value: '2588584',
            caption: ' Københavns Politi'
        },
        {
            value: '9314927',
            caption: ' Københavns Vestegns Politi'
        },
        {
            value: '7444313',
            caption: ' Midt- og Vestjyllands Politi'
        },
        {
            value: '4809056',
            caption: ' Midt- og Vestsjællands Politi'
        },
        {
            value: '3415037',
            caption: ' Nordjyllands Politi'
        },
        {
            value: '3147202',
            caption: ' Nordsjællands Politi'
        },
        {
            value: '5169508',
            caption: ' Syd- og Sønderjyllands Politi'
        },
        {
            value: '4122991',
            caption: ' Sydsjællands- og Lolland- Falsters Politi'
        },
        {
            value: '8008059',
            caption: ' Sydøstjyllands Politi'
        },
        {
            value: '0699234',
            caption: ' Østjyllands Politi'
        },
        {
            value: 'U',
            caption: ' Ved ikke'
        }
    ];
};

const PoliceDistrict = ({ elementId, caption, placeholder, helpText }: PoliceDistrictsType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = PoliceDistrict.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    type Options = {
        value: string;
        caption: string;
    };

    const [policeDistricts, setPoliceDistricts] = React.useState<Options[]>([]);

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        const districts = getPoliceDistrictArray();
        setPoliceDistricts(districts);
    }, []);

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = PoliceDistrict.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <SelectField
                required={isRequiredField}
                label={placeholder}
                id={elementId}
                key={elementId}
                options={policeDistricts.map((option) => ({
                    label: option.caption,
                    value: option.value
                }))}
                isValid={isValid}
                helpText={helpText}
                errorText={errorMessage}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                value={value}
            />
        </>
    );
};

PoliceDistrict.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as PoliceDistrictsType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    const districts = getPoliceDistrictArray();
    const option = districts.find((item) => item.value === value);
    const caption = option?.caption;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={caption} />;
};

PoliceDistrict.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const value = PoliceDistrict.getValue(formValues, elementId);
    const valueStr = PoliceDistrict.getValueHuman(formValues, elementId);

    const valueId = { string: value };

    return getClaimFormSubmitValue('policedistricts', elementId, label, valueStr, valueId);
};

PoliceDistrict.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

PoliceDistrict.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = PoliceDistrict.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

PoliceDistrict.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = PoliceDistrict.getValue(formValues, elementId);
    return value;
};

PoliceDistrict.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

PoliceDistrict.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = PoliceDistrict.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default PoliceDistrict;
